import { useMutation, useQueryClient } from '@tanstack/react-query';
import { FC, useState } from 'react';

import type { TFetchError } from '@/api/api-error.types';
import { _replaceTeamMemberGoals } from '@/api/gamma-team/mutations';
import {
  type ActivityCounts,
  GAMMA_TEAM_QUERY_KEYS,
  TEAM_GOAL_METRICS,
  type TeamGoal,
  type TeamGoalCadence,
  teamGoalCadence,
  type TeamGoalInput,
  type TeamGoalMetric,
} from '@/api/gamma-team/types';
import CloseIcon from '@/assets/svg/ui-elements/close.svg';
import {
  Alert,
  Button,
  IconButton,
  InputNumber,
  Progress,
  Select,
  TextButton,
  theme,
  Typography,
} from '@/design-system';

import {
  GoalEditorRow,
  GoalRow,
  QuietLine,
  Section,
  SectionActions,
  SectionHeader,
} from './styled';
import { goalProgress, type GoalStatus, METRIC_LABELS } from './team-trends';

export const GOALS_HEADING = 'Goals';
export const EDIT_GOALS_LABEL = 'Edit goals';
export const SAVE_GOALS_LABEL = 'Save goals';

const CADENCE_LABELS: Record<TeamGoalCadence, string> = {
  weekly: 'per week',
  monthly: 'per month',
};

const STATUS_COPY: Record<GoalStatus, { label: string; color: 'success' | 'brand' | 'warning' }> = {
  met: { label: 'Goal met', color: 'success' },
  onTrack: { label: 'On track', color: 'brand' },
  behind: { label: 'Behind', color: 'warning' },
};

const METRIC_OPTIONS = TEAM_GOAL_METRICS.map((metric) => ({
  value: metric,
  label: METRIC_LABELS[metric],
}));

const CADENCE_OPTIONS = [
  { value: teamGoalCadence.Weekly, label: 'Per week' },
  { value: teamGoalCadence.Monthly, label: 'Per month' },
];

const MAX_TARGET = 100_000;

const isConflict = (error: unknown): boolean => {
  const failure = error as TFetchError | null | undefined;
  return (failure?.status ?? failure?.data?.statusCode) === 409;
};

const toGoalInput = ({ metric, cadence, target }: TeamGoal): TeamGoalInput => ({
  metric,
  cadence,
  target,
});

const nextFreeMetric = (drafts: readonly TeamGoalInput[]): TeamGoalMetric | null =>
  TEAM_GOAL_METRICS.find((metric) => !drafts.some((draft) => draft.metric === metric)) ?? null;

interface GoalEditorProps {
  userId: string;
  name: string;
  goals: TeamGoal[];
  onDone: () => void;
}

const GoalEditor: FC<GoalEditorProps> = ({ userId, name, goals, onDone }) => {
  const queryClient = useQueryClient();
  const [drafts, setDrafts] = useState<TeamGoalInput[]>(() => goals.map(toGoalInput));
  const save = useMutation({
    mutationFn: _replaceTeamMemberGoals,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [GAMMA_TEAM_QUERY_KEYS.goals] });
      onDone();
    },
    onError: (error) => {
      if (isConflict(error)) {
        void queryClient.invalidateQueries({ queryKey: [GAMMA_TEAM_QUERY_KEYS.goals] });
      }
    },
  });

  const update = (index: number, patch: Partial<TeamGoalInput>) =>
    setDrafts((current) =>
      current.map((draft, at) => (at === index ? { ...draft, ...patch } : draft)),
    );
  const remove = (index: number) => setDrafts((current) => current.filter((_, at) => at !== index));
  const add = () => {
    const metric = nextFreeMetric(drafts);
    if (metric) setDrafts((current) => [...current, { metric, cadence: 'weekly', target: 10 }]);
  };

  const duplicate = new Set(drafts.map((draft) => draft.metric)).size !== drafts.length;
  const invalid = duplicate || drafts.some((draft) => !(draft.target >= 1));

  return (
    <Section aria-label={`Edit goals for ${name}`}>
      {drafts.length === 0 && (
        <Typography variant="body3" color={theme.colors.text.secondary}>
          No goals. Add one to track progress against it.
        </Typography>
      )}
      {drafts.map((draft, index) => (
        <GoalEditorRow key={index}>
          <Select
            size="md"
            label="Metric"
            value={draft.metric}
            options={METRIC_OPTIONS.map((option) => ({
              ...option,
              disabled:
                option.value !== draft.metric &&
                drafts.some((other) => other.metric === option.value),
            }))}
            onChange={(value) => update(index, { metric: value as TeamGoalMetric })}
          />
          <InputNumber
            size="md"
            fullWidth
            label="Target"
            min={1}
            max={MAX_TARGET}
            value={draft.target}
            onValueChange={(value) => update(index, { target: value ?? 0 })}
          />
          <Select
            size="md"
            label="Cadence"
            value={draft.cadence}
            options={CADENCE_OPTIONS}
            onChange={(value) => update(index, { cadence: value as TeamGoalCadence })}
          />
          <IconButton
            size="sm"
            variant="ghost"
            color="tertiary"
            aria-label={`Remove the ${METRIC_LABELS[draft.metric]} goal`}
            onClick={() => remove(index)}
          >
            <CloseIcon width={16} height={16} />
          </IconButton>
        </GoalEditorRow>
      ))}
      {duplicate && (
        <Typography variant="body4" color={theme.colors.error.text}>
          Each metric can have only one goal.
        </Typography>
      )}
      {save.isError && (
        <Alert
          variant="error"
          description={
            isConflict(save.error)
              ? 'Someone else changed these goals at the same time. Check them and save again.'
              : 'The goals couldn’t be saved. Try again.'
          }
        />
      )}
      <SectionActions>
        <TextButton color="neutral" disabled={!nextFreeMetric(drafts)} onClick={add}>
          Add goal
        </TextButton>
        <Button variant="ghost" color="tertiary" size="sm" onClick={onDone}>
          Cancel
        </Button>
        <Button
          size="sm"
          disabled={invalid}
          loading={save.isPending}
          onClick={() => save.mutate({ userId, goals: drafts, expected: goals.map(toGoalInput) })}
        >
          {SAVE_GOALS_LABEL}
        </Button>
      </SectionActions>
    </Section>
  );
};

interface MemberGoalsProps {
  userId: string;
  name: string;
  goals: TeamGoal[] | undefined;
  failed?: boolean;
  onRetry?: () => void;
  counts: ActivityCounts | null;
  reportedDays: number;
  canEdit: boolean;
}

export const MemberGoals: FC<MemberGoalsProps> = ({
  userId,
  name,
  goals,
  failed = false,
  onRetry,
  counts,
  reportedDays,
  canEdit,
}) => {
  const [editing, setEditing] = useState(false);
  const own = goals?.filter((goal) => goal.userId === userId) ?? [];

  return (
    <Section aria-label={GOALS_HEADING}>
      <SectionHeader>
        <Typography variant="body1" fontWeight={600}>
          {GOALS_HEADING}
        </Typography>
        {canEdit && !editing && goals && (
          <TextButton color="neutral" onClick={() => setEditing(true)}>
            {EDIT_GOALS_LABEL}
          </TextButton>
        )}
      </SectionHeader>

      {editing ? (
        <GoalEditor userId={userId} name={name} goals={own} onDone={() => setEditing(false)} />
      ) : !goals && failed ? (
        <QuietLine role="alert">
          <Typography variant="body3" color={theme.colors.text.secondary}>
            Goals couldn’t be loaded.
          </Typography>
          {onRetry && (
            <Button variant="outline" color="tertiary" size="sm" onClick={onRetry}>
              Try again
            </Button>
          )}
        </QuietLine>
      ) : !goals ? (
        <Typography variant="body3" color={theme.colors.text.secondary}>
          Loading goals…
        </Typography>
      ) : own.length === 0 ? (
        <Typography variant="body3" color={theme.colors.text.secondary}>
          {canEdit ? 'No goals yet. Set one to track progress.' : 'No goals set.'}
        </Typography>
      ) : (
        own.map((goal) => {
          const progress = counts ? goalProgress(goal, counts, reportedDays) : null;
          const status = progress ? STATUS_COPY[progress.status] : null;
          return (
            <GoalRow key={goal.metric}>
              <Progress
                size="sm"
                color={status?.color ?? 'brand'}
                value={progress ? Math.min(100, Math.round(progress.ratio * 100)) : 0}
                label={`${METRIC_LABELS[goal.metric]} · ${goal.target} ${CADENCE_LABELS[goal.cadence]}`}
                progressLabel={
                  progress && status
                    ? `${progress.actual} of ${progress.expected} · ${status.label}`
                    : '—'
                }
              />
            </GoalRow>
          );
        })
      )}
    </Section>
  );
};
